import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ApiService } from '../../../core/services/api.service';
import { PermissionService } from '../../../core/services/permission.service';
import { EActionEvent } from '../../../core/enums/table.enum';
import { EStatus } from '../../../core/enums/status.enum';
import { IDataColumn, IDataSourceConfig } from '../../../core/interfaces/table.interface';
import { Subject, Subscription, debounceTime, distinctUntilChanged, filter, map } from 'rxjs';
import { StatusBadgePipe } from '../../../core/pipes/status-badge.pipe';


@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() config!: IDataSourceConfig;
  @Input() data: any[] | undefined = [];
  @Input() serverSide = false;
  @Input() title = '';
  @Input() isLoading = false;
  @Input() showSearch = true;
  @Input() showFilter = true;
  @Input() searchPlaceholder = 'Search...';
  @Input() searchDebounceTime = 500;
  @Input() filterOptions: Array<{
    label: string;
    key: string;
    options: any[];
  }> = [];
  @Input() totalRecords = 0;
  @Input() totalPages = 0;

  EActionEvent = EActionEvent;
  EStatus = EStatus;

  @Output() actionClick = new EventEmitter<{ action: EActionEvent; row: any }>();
  @Output() approve = new EventEmitter<any>();
  @Output() reject = new EventEmitter<any>();
  @Output() searchChange = new EventEmitter<any>();
  @Output() filtersChange = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<{ pageNumber: number; pageSize: number }>();

  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];
  expandedRow: any | null = null;
  loadingExpand = false;
  expandedDetails: any = null;
  isFilterOpen = false;
  selectedFilter: any = null;
  searchValue = '';
  FilterStates: { [key: string]: { isOpen: boolean; selected: any } } = {};
  selectedFilters: { [key: string]: any } = {};

  private readonly statusSet = new Set<string>(Object.values(EStatus) as string[]);
  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription | null = null;

  constructor(
    private api: ApiService,
    private permissionService: PermissionService
  ) { }

  ngOnInit(): void {
    this.dataSource.data = this.data || [];
    this.setupSearchDebounce();
    this.initializeFilters();
  }

  private initializeFilters(): void {
    this.filterOptions.forEach(filter => {
      this.FilterStates[filter.key] = { isOpen: false, selected: null };
      this.selectedFilters[filter.key] = null;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataSource.data = this.data || [];
    if (changes['filterOptions'] && this.filterOptions) {
      this.initializeFilters();
    }
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  private setupSearchDebounce(): void {
    this.searchSubscription = this.searchSubject
      .pipe(
        map((term: string) => term.trim()),
        filter((term) => term.length >= 1),
        debounceTime(this.searchDebounceTime),
        distinctUntilChanged()
      )
      .subscribe((searchTerm) => {
        this.searchChange.emit(searchTerm);
      });
  }


  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchValue = input.value;

    if (this.serverSide) {
      // Apply search event for server-side filtering
      this.searchSubject.next(this.searchValue);
    } else {
      // Apply client-side filtering
      this.dataSource.filter = (this.searchValue || '').trim().toLowerCase();
    }
  }

  clearSearch(): void {
    this.searchValue = '';
    if (this.serverSide) {
      this.searchChange.emit('');
    } else {
      this.dataSource.filter = '';
    }
  }

  onFilterClick(filterKey: string): void {
    this.FilterStates[filterKey].isOpen = !this.FilterStates[filterKey].isOpen;
    if (this.FilterStates[filterKey].isOpen) {
      this.selectedFilter = filterKey;
    } else {
      this.selectedFilter = null;
    }

  }

  closeFilter(filterKey: string): void {
    this.FilterStates[filterKey].isOpen = false;
    this.selectedFilter = null;
  }

  onFilterSelect(filterKey: string, value: any): void {
    this.FilterStates[filterKey].selected = value;
    this.FilterStates[filterKey].isOpen = false;
    this.selectedFilters[filterKey] = value;

    if (this.serverSide) {
      this.filtersChange.emit({ ...this.selectedFilters });
    } else {
      this.applyClientSideMultiFilter();
    }
  }

  clearFilter(filterKey: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.FilterStates[filterKey].selected = null;
    this.selectedFilters[filterKey] = null;

    if (this.serverSide) {
      this.filtersChange.emit({ ...this.selectedFilters });
    } else {
      this.applyClientSideMultiFilter();
    }
  }

  isFilterSelected(filterKey: string, value: any): boolean {
    return this.FilterStates[filterKey]?.selected === value || false;
  }

  getSelectedFilterCount(filterKey: string): number {
    return this.FilterStates[filterKey]?.selected ? 1 : 0;
  }

  applyClientSideMultiFilter(): void {
    this.dataSource.filterPredicate = (data: any, _filter: string) => {
      for (const key in this.selectedFilters) {
        const filterValue = this.selectedFilters[key];
        if (filterValue) {
          const dataValue = (data[key] || '').toString().toUpperCase();
          if (dataValue !== filterValue.toString().toUpperCase()) {
            return false;
          }
        }
      }
      return true;
    };
    this.dataSource.filter = JSON.stringify(this.selectedFilters);
  }

  clearAllFilters(): void {
    this.selectedFilter = null;
    this.searchValue = '';

    this.filterOptions.forEach(filter => {
      this.FilterStates[filter.key].selected = null;
      this.selectedFilters[filter.key] = null;
    });

    if (this.serverSide) {
      this.searchChange.emit('');
      this.filtersChange.emit({});
    } else {
      this.dataSource.filter = '';
    }
  }

  hasActiveAdditionalFilters(): boolean {
    return Object.values(this.selectedFilters).some(value => value !== null && value !== undefined && value !== '');
  }

  onAction(action: EActionEvent, row: any) {
    this.actionClick.emit({ action, row });

    if (action === EActionEvent.EYE) {
      this.toggleExpand(row);
    }
  }

  // Expand row
  async toggleExpand(row: any) {
    this.expandedRow = this.expandedRow === row ? null : row;
    if (!this.expandedRow) {
      this.expandedDetails = null;
      return;
    }
    if (!row.__fullDetails) {
      this.loadingExpand = true;
      try {
        const details: any = await this.api.getApplicationById(row.id, row.serviceCode).toPromise();
        row.__fullDetails = details;
        this.expandedDetails = details;
      } catch (err) {
        console.error('fetch details failed', err);
        this.expandedDetails = { error: true };
      } finally {
        this.loadingExpand = false;
      }
    } else {
      this.expandedDetails = row.__fullDetails;
    }
  }

  shouldShowActionIcon(action: EActionEvent, row: any): boolean {
    if (!action) return false;

    const normalizedStatus = this.normalizeStatus(row.status || row.applicationStatus || row.expressionOfInterestStatus);
    const licenseCerticateStatus = this.normalizeStatus(row.summaryData?.licenseStatus);
    const isFeeField = row.feeType !== undefined && row.feeType !== null && row.feeType !== '';
    const isLicense = row.license !== undefined && row.license !== null && row.license !== '';

    if (action === EActionEvent.DOWNLOAD) {
      if (normalizedStatus === EStatus.DRAFT) return false;
      const hasCertificate = !!(row.summaryData?.certificateId || row.summaryData?.certificate);
      return hasCertificate;
    }

    if (action === EActionEvent.EYE) {
      const isNotPaymentPending = normalizedStatus !== EStatus.DRAFT;
      return isNotPaymentPending;
    }

    if (action === EActionEvent.EDIT) {
  if (isFeeField) return true;

  if (
    !normalizedStatus ||
    ![EStatus.QUERIED, EStatus.DRAFT, EStatus.PENDING_VERIFICATION].includes(normalizedStatus)
  ) {
    return false;
  }

  return this.permissionService.hasPermission([
    'APPLICATION_CREATE',
  ]);
}

    if (action === EActionEvent.APPROVE) {
      const canShowForStatus =
        normalizedStatus === EStatus.PENDING_REGISTRY_APPROVAL ||
        normalizedStatus === EStatus.PENDING_REGISTRAR_APPROVAL ||
        normalizedStatus === EStatus.PARTIALLY_APPROVED;
      if (!canShowForStatus) return false;

      return this.permissionService.hasPermission('APPLICATION_APPROVE');
    }

    if (action === EActionEvent.REJECT) {
      const canShowForStatus =
        normalizedStatus === EStatus.PENDING_REGISTRY_APPROVAL ||
        normalizedStatus === EStatus.PENDING_REGISTRAR_APPROVAL ||
        normalizedStatus === EStatus.PARTIALLY_APPROVED;
      if (!canShowForStatus) return false;

      return this.permissionService.hasPermission([
        'APPLICATION_QUERY',
        'APPLICATION_REJECT'
      ]);
    }

    if (action === EActionEvent.RETRY) {
      if (normalizedStatus !== EStatus.DRAFT) return false;
      return this.permissionService.hasPermission([
        'APPLICATION_CREATE',
        'COMPANY_CREATE'
      ]);
    }

    if (action === EActionEvent.REVIEW) {
      if (isLicense && normalizedStatus !== EStatus.APPROVED) return false;
      return this.permissionService.hasPermission(['APPLICATION_APPROVE',
        'APPLICATION_REJECT',
        'APPLICATION_QUERY']);
    }

    if (action === EActionEvent.APPLY_NOW) {
      const status = this.normalizeStatus(row.status);
      const expressionOfInterestStatus = this.normalizeStatus(row.expressionOfInterestStatus);

      const canShow =
        status === EStatus.SERVICE_ACCEPTED ||
        expressionOfInterestStatus === EStatus.APPROVED;

      if (!canShow) return false;

      return this.permissionService.hasPermission([
        'APPLICATION_CREATE',
        'COMPANY_CREATE'
      ]);
    }

    if (action === EActionEvent.PAY_CERTIFICATE) {
      if (!licenseCerticateStatus) return false;

      const isInactive = licenseCerticateStatus === EStatus.INACTIVE;
      const isApproved = normalizedStatus === EStatus.FINALLY_APPROVED;

      if (!isInactive || !isApproved) return false;

      return this.permissionService.hasPermission([
        'APPLICATION_CREATE',
        'COMPANY_CREATE'
      ]);
    }

    return false;

    return false;
  }

  getDownloadRow(row: any, file: any) {
    return {
      ...row,
      certificateId: file.attachmentId,
    };
  }

  get actionField(): IDataColumn | null {
    if (!this.config || !Array.isArray(this.config.fields)) return null;
    return this.config.fields.find((f) => !!f.isAction) || null;
  }

  private normalizeStatus(value: any): EStatus | null {
    if (value === null || value === undefined) {
      return null;
    }
    const normalized = value.toString().toUpperCase().trim();
    return this.statusSet.has(normalized) ? (normalized as EStatus) : null;
  }

  //paginator
  currentPage = 1;
  pageSize = 10;

  get paginatedData() {
    const total = this.totalRecords || this.dataSource.data?.length || 0;
    this.currentPageStart = (this.currentPage - 1) * this.pageSize;
    this.currentPageEnd = Math.min(this.currentPageStart + this.dataSource.data.length, total);
    return this.dataSource.data;
  }

  get effectiveTotalPages(): number {
    return this.totalPages
  }

  get effectiveTotalRecords(): number {
    return this.totalRecords
  }

  currentPageStart = 0;
  currentPageEnd = 0;

  nextPage() {
    const maxPages = this.effectiveTotalPages;
    if (this.currentPage < maxPages) {
      this.currentPage++;
      if (this.serverSide) {
        this.pageChange.emit({ pageNumber: this.currentPage, pageSize: this.pageSize });
      }
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      if (this.serverSide) {
        this.pageChange.emit({ pageNumber: this.currentPage, pageSize: this.pageSize });
      }
    }
  }

  getStatusClass(status: string): string {
    return new StatusBadgePipe().transform(status).cssClass;
  }

  getActionIcon(action: EActionEvent): { icon: string; color: string } {
    switch (action) {
      case EActionEvent.EDIT:
        return { icon: 'edit', color: 'text-yellow-600' };

      case EActionEvent.DOWNLOAD:
        return { icon: 'download', color: 'text-green-600' };

      case EActionEvent.INFO:
        return { icon: 'info', color: 'text-gray-600' };

      case EActionEvent.EYE:
        return { icon: 'visibility', color: 'text-blue-600' };

      case EActionEvent.APPROVE:
        return { icon: 'check_circle', color: 'text-green-600' };

      case EActionEvent.REJECT:
        return { icon: 'cancel', color: 'text-red-600' };
      case EActionEvent.RETRY:
        return { icon: 'refresh', color: 'text-blue-600' };
      case EActionEvent.REVIEW:
        return { icon: 'fact_check', color: 'text-green-600' };
      case EActionEvent.APPLY_NOW:
        return { icon: 'play_arrow', color: 'text-blue-600' };
      case EActionEvent.PAY_CERTIFICATE:
        return { icon: 'payment', color: 'text-yellow-600' };

      default:
        return { icon: 'help_outline', color: 'text-gray-400' };
    }
  }

  getActionTooltip(action: EActionEvent): string {
    switch (action) {
      case EActionEvent.EDIT:
        return 'Edit application';
      case EActionEvent.DOWNLOAD:
        return 'Download certificate';
      case EActionEvent.INFO:
        return 'View information';
      case EActionEvent.EYE:
        return 'View details';
      case EActionEvent.APPROVE:
        return 'Approve application';
      case EActionEvent.REJECT:
        return 'Reject application';
      case EActionEvent.RETRY:
        return 'Retry payment';
      default:
        return 'Action';
    }
  }

}
